/* ============================================================================
   Kawaii kit: shared behaviour for the 2026 redesign. Loaded (deferred) by
   layout/theme.liquid whenever the new design is on.

   1. Add-to-cart feedback. Kit add buttons sit inside the theme's
      <add-to-cart> element (global.js), which does the request, refreshes
      the cart bubble and opens the drawer. It announces the result with
      document events; this turns the matching buttons into "Added" and
      shows errors (sold out, quantity limits), which the theme otherwise
      drops silently.

   2. Shop hours. Anything marked [data-kc-hours] gets data-state="open" or
      "closed" from the shop's hours (Theme settings > Redesign 2026), in the
      shop's time zone, rechecked every minute. Liquid renders a first guess
      so nothing flips on load unless the cached page is out of date.

   3. Preview: the new cart drawer on ?view= pages. sections/mini-cart.liquid
      picks the new drawer from template.suffix, and only a plain section
      request (/?section_id=mini-cart&view=kawaii) carries the view. The
      sections Shopify sends back with a cart change (add, quantity, remove:
      "bundled section rendering") always come from the default template,
      whatever sections_url says (checked on the dev theme, 2026-10-11), so
      after every cart change the drawer came back as the old one. On ?view=
      pages this adds the view to the theme's section requests, and after a
      cart change that asked for the drawer, swaps the old drawer in the
      answer for a fresh section request. With the design on everywhere
      (Theme settings > Redesign 2026) the drawer is right first time and
      pages carry no ?view=, so this does nothing.
   ============================================================================ */
(() => {
  if (window.kawaiiKit) return;
  window.kawaiiKit = true;

  /* --- 1. add to cart ------------------------------------------------- */

  let toast;
  let toastTimer;

  function showToast(message) {
    if (!message) return;
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'kc-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-on'), 5000);
  }

  document.addEventListener('ajaxProduct:added', (event) => {
    const item = event.detail && event.detail.product;
    if (!item) return;
    const id = String(item.variant_id || item.id);
    document
      .querySelectorAll(`add-to-cart[data-kc-add][data-variant-id="${CSS.escape(id)}"]`)
      .forEach((el) => el.classList.add('is-added'));
  });

  document.addEventListener('ajaxProduct:error', (event) => {
    showToast(event.detail && event.detail.errorMessage);
  });

  /* --- 2. shop hours -------------------------------------------------- */

  function minutesNow(timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(new Date());
      const get = (type) => Number(parts.find((p) => p.type === type).value);
      return (get('hour') % 24) * 60 + get('minute');
    } catch (e) {
      const d = new Date();
      return d.getHours() * 60 + d.getMinutes();
    }
  }

  function updateHours() {
    document.querySelectorAll('[data-kc-hours]').forEach((el) => {
      const open = Number(el.dataset.open) * 60;
      const close = Number(el.dataset.close) * 60;
      const now = minutesNow(el.dataset.tz || 'America/Chicago');
      const isOpen = close > open ? now >= open && now < close : now >= open || now < close;
      el.dataset.state = isOpen ? 'open' : 'closed';
    });
  }

  function startHours() {
    updateHours();
    setInterval(updateHours, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startHours);
  } else {
    startHours();
  }

  document.addEventListener('shopify:section:load', updateHours);

  /* --- 3. preview: the new drawer on ?view= pages --------------------- */

  const view = new URLSearchParams(window.location.search).get('view');
  if (view && typeof window.fetch === 'function') {
    const nativeFetch = window.fetch;
    const root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    const hasView = (u) => /[?&]view=/.test(u);
    const withView = (u) => `${u}${u.indexOf('?') === -1 ? '?' : '&'}view=${encodeURIComponent(view)}`;
    // Section requests for this site's pages; not recommendations or search,
    // which aren't templates.
    const isSectionGet = (u) =>
      /[?&]sections?(_id)?=/.test(u) &&
      !/\/(recommendations|search)\b/.test(u.split('?')[0]) &&
      (u.charAt(0) === '/' || u.indexOf(window.location.origin) === 0);
    const isCartChange = (u, init) =>
      Boolean(init) && String(init.method || '').toUpperCase() === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u);

    // Did this cart request ask for the drawer back? JSON (theme, kit) or
    // FormData (product forms) bodies, sections as a list or a string.
    const asksForDrawer = (body) => {
      let sections = '';
      if (body instanceof FormData) {
        sections = String(body.get('sections') || '');
      } else if (typeof body === 'string' && body.indexOf('"sections"') !== -1) {
        const data = JSON.parse(body);
        sections = Array.isArray(data.sections) ? data.sections.join(',') : String(data.sections || '');
      }
      return sections.split(',').some((id) => id.trim() === 'mini-cart');
    };

    const freshDrawer = async (response) => {
      try {
        const data = await response.clone().json();
        const html = data && data.sections && data.sections['mini-cart'];
        if (typeof html !== 'string' || html.indexOf('<kc-bag') !== -1) return response;
        const fresh = await nativeFetch.call(window, withView(`${root}?section_id=mini-cart`));
        if (!fresh.ok) return response;
        data.sections['mini-cart'] = await fresh.text();
        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (e) {
        return response;
      }
    };

    window.fetch = function kcFetch(input, init) {
      let url = input instanceof URL ? input.href : input;
      let drawer = false;
      try {
        if (typeof url === 'string' && isSectionGet(url) && !hasView(url)) url = withView(url);
        if (typeof url === 'string' && isCartChange(url, init) && asksForDrawer(init.body)) drawer = true;
      } catch (e) {
        /* leave the request as it was */
      }
      const request = nativeFetch.call(window, url, init);
      return drawer ? request.then(freshDrawer) : request;
    };
  }
})();
